import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { cloneDeep } from 'lodash-es';
import { generate } from 'generate-password-browser';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CustomValidator } from '../../../../../shared/validators/custom_validator';
import { Country } from '../../../../../models/interfaces/country.interface';
import { UtilsService } from '../../../../../shared/utils/utils.service';
import { DateTime } from 'luxon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';

@Component({
    selector: 'app-modal-edit-student-data',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatDialogModule,
        MatProgressSpinnerModule,
        MatAutocompleteModule,
        MatSelectModule,
        MatOptionModule
    ],
    templateUrl: './modal-edit-student-data.component.html',
    styleUrl: './modal-edit-student-data.component.scss'
})
export class ModalEditStudentDataComponent implements OnInit {
  studentForm: FormGroup = new FormGroup({});
  showCloseAlert = false;
  isSaving = false;
  studentId!: string;
  fullname!: string;
  hidePassword = false;
  countries: Array<Country> = [];
  filteredCountries: Array<Country> = [];
  minBirthdate = DateTime.now().minus({ years: 100 }).toFormat('yyyy-LL-dd');
  maxBirthdate = DateTime.now().minus({ years: 14 }).toFormat('yyyy-LL-dd');

  displayFn = (countryId: string): string => {
    const country = this.filteredCountries.find((country) => country.countryId === countryId);
    return country && country.name ? country.name : '';
  };

  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalEditStudentDataComponent>,
    private alert: AlertService,
    private api: ApiService,
    private toast: ToastService,
    private utils: UtilsService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  initForm(): void {
    const receivedData = this.data ? cloneDeep(this.data) : null;
    const { student, countries } = receivedData;
    if (!student) {
      this.closeModal();
      return;
    }

    if (countries?.length) {
      this.countries = countries;
      this.filteredCountries = cloneDeep(this.countries);
    }

    this.studentId = student.userId;
    this.fullname = `${student.name} ${student.lastname}`;

    this.studentForm = new FormGroup(
      {
        email: new FormControl(student.email || null, [Validators.email, Validators.required]),
        name: new FormControl(student.name || null, Validators.required),
        lastname: new FormControl(student.lastname || null, Validators.required),
        countryId: new FormControl(student.countryId || null),
        city: new FormControl(student.city || null),
        dni: new FormControl(student.dni || null),
        address: new FormControl(student.address || null),
        phone: new FormControl(student.phone || null),
        phonePrefix: new FormControl(student.phonePrefix || null),
        gender: new FormControl(student.gender || null),
        birthdate: new FormControl(student.birthdate || null),
        password: new FormControl(null)
      },
      { validators: [CustomValidator.validateAge] }
    );

    this.studentForm.controls.countryId.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((countryId: string) => {
      if (countryId) {
        const country = this.countries.find((country) => country.countryId === countryId);
        if (!country) return;

        this.studentForm.controls.phonePrefix.setValue(country.phoneCode);
      } else {
        this.studentForm.controls.phonePrefix.setValue(null);
      }
    });

    this.listenForChanges();

    setTimeout(() => {
      const element = document.getElementById('phone-field');
      if (!element) return;
      element.removeAttribute('readonly');
    }, 1000);
  }

  listenForChanges(): void {
    this.studentForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      this.showCloseAlert = true;
    });
  }

  async confirmCloseModal(): Promise<any> {
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }

    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  generateRandomPassword(): void {
    const randomPassword = generate({
      lowercase: true,
      numbers: true,
      uppercase: true,
      symbols: false,
      length: 10,
      strict: true
    });
    this.studentForm.controls.password.setValue(randomPassword);
  }

  filterCountry(event: any): void {
    const countryToSearch = event?.target?.value ? event.target.value.trim().toLowerCase() : null;
    if (!this.utils.hasSomething(countryToSearch)) {
      this.filteredCountries = cloneDeep(this.countries);
      return;
    }

    const countriesFound = this.countries.filter((country) => country.name.trim().toLowerCase().includes(countryToSearch));
    this.filteredCountries = cloneDeep(countriesFound);
  }

  getFlagFromCountryId(countryId: string) {
    const country = this.filteredCountries.find((country) => country.countryId === countryId);
    return country?.flag ? country.flag : null;
  }

  async saveStudentData(): Promise<void> {
    try {
      this.isSaving = true;
      const body = {
        studentId: this.studentId,
        ...this.studentForm.value
      };
      const response = await firstValueFrom(this.api.saveStudentData(body));
      this.isSaving = false;
      this.closeModal({ studentUpdated: this.studentForm.value });
      this.toast.presentSuccessToast(response.message);
    } catch (errorResponse: any) {
      this.isSaving = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error guardando información del estudiante');
    }
  }
}
